import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Mail } from "lucide-react";

export default function DryIcePelletizerPage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await publicApi.getProducts({ category: "dry-ice-pelletizer" });
                setProducts(res.data);
            } catch (err) {
                console.error("Failed to fetch pelletizers:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    if (loading) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-[70vh]" data-testid="dry-ice-pelletizer-page">
            <div className="bg-card border-b border-border">
                <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20">
                    <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Production Systems</p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-foreground tracking-tight mb-4">
                        DRY ICE <span className="text-primary">PELLETIZER</span>
                    </h1>
                    <p className="text-muted-foreground text-base lg:text-lg max-w-2xl">
                        Industrial pelletizer systems for on-site dry ice production. Convert liquid CO₂ into high-quality dry ice pellets.
                    </p>
                </div>
            </div>

            <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20">
                {products.length === 0 ? (
                    <div className="bg-card border border-border p-8 lg:p-12 text-center max-w-2xl mx-auto">
                        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                            Contact us for information on dry ice pelletizer systems, pricing, and installation.
                        </p>
                        <Link to="/quote">
                            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8">
                                Contact Us
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {products.map((product, index) => (
                            <Link key={product.id} to={`/products/${product.id}`} data-testid={`pelletizer-card-${index}`}>
                                <Card className="bg-card border border-border rounded-none group hover:border-primary/50 transition-colors overflow-hidden h-full">
                                    <div className="aspect-[16/10] bg-secondary overflow-hidden">
                                        {product.images?.[0] ? (
                                            <img
                                                src={resolveImageUrl(product.images[0])}
                                                alt={product.display_title}
                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <span className="text-muted-foreground font-mono text-sm">No Image</span>
                                            </div>
                                        )}
                                    </div>
                                    <CardContent className="p-6">
                                        <div className="flex items-start justify-between gap-4 mb-2">
                                            <p className="font-mono text-xs text-muted-foreground">{product.internal_name}</p>
                                            {product.is_featured && (
                                                <Badge className="rounded-none bg-primary/10 text-primary border-0">Featured</Badge>
                                            )}
                                        </div>
                                        <h3 className="font-heading font-bold text-xl mb-2">{product.display_title}</h3>
                                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{product.short_description}</p>

                                        {product.manufacturer && (
                                            <p className="text-muted-foreground/60 text-xs mb-4">
                                                Manufactured by <span className="text-foreground">{product.manufacturer}</span> · Official Sales & Service by <span className="text-foreground">{product.sales_service_provider || "Dry Ice Bulls"}</span>
                                            </p>
                                        )}

                                        <div className="flex items-center justify-between pt-4 border-t border-border">
                                            <span className="font-mono text-primary font-bold">{product.price_text}</span>
                                            <Button variant="ghost" size="sm" className="rounded-none text-primary hover:bg-primary/10">
                                                View Details <ArrowRight className="ml-1 h-4 w-4" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
